import { useQuery } from '@tanstack/react-query';

import { logInquiryApi } from '../api/log.adapter';
import type { LogInquiryListFilters } from '../store/log-inquiry-list.store';
import type {
  ListLogsParams,
  ListLogsResult,
  LogSortDir,
  LogSortKey,
} from '../types/log.types';

// The query-key filters: everything in ListLogsParams EXCEPT the paging and
// sorting fields, which are passed separately so the cache key can distinguish
// "same filters, different page" from "different filters".
export type LogListFilters = Omit<ListLogsParams, 'page' | 'size' | 'sort' | 'dir'>;

// Normalize the store's UI filter state to wire params: 'all'/empty → undefined
// (omitted = no filter). One source of truth for both the list query and the
// CSV export, so the exported rows always match what is on screen.
export function toQueryFilters(filters: LogInquiryListFilters): LogListFilters {
  return {
    search: filters.search.trim() || undefined,
    source: filters.source === 'all' ? undefined : filters.source,
    userType: filters.userType === 'all' ? undefined : filters.userType,
    category: filters.category === 'all' ? undefined : filters.category,
    status: filters.status === 'all' ? undefined : filters.status,
    dateFrom: filters.dateFrom ?? undefined,
    dateTo: filters.dateTo ?? undefined,
  };
}

// Cache keys — `all` is the broad-invalidate root; `list(...)` keeps each
// filter + page + sort combination independently cached, so stepping back to a
// page already visited is instant.
export const logInquiryQueryKeys = {
  all: ['logs'] as const,
  list: (
    filters: LogListFilters,
    page: number,
    sort: LogSortKey,
    dir: LogSortDir,
  ) => [...logInquiryQueryKeys.all, 'list', filters, page, sort, dir] as const,
};

// One numbered page of the log (FEATURE_050). The BE serves offset pages with a
// total row count, so a plain query — not an infinite one — is the right fit:
// each page is a separate fetch keyed on its own page number and sort order.
//
// `placeholderData` keeps the previous page's rows on screen while the next one
// loads, so clicking through pages does not flash the loading skeleton.
export function useLogsQuery(
  filters: LogListFilters,
  page: number,
  size: number,
  sort: LogSortKey,
  dir: LogSortDir,
) {
  return useQuery<ListLogsResult>({
    queryKey: logInquiryQueryKeys.list(filters, page, sort, dir),
    queryFn: () => logInquiryApi.listLogs({ ...filters, page, size, sort, dir }),
    placeholderData: (previous) => previous,
  });
}
